import { useCallback, useEffect, useRef, useState } from "react";

const MP4_SRC = "/images/vault-open-bumper.mp4?v=4";
const POSTER_SRC = "/images/intro-poster.jpg";
const SAFETY_MS = 20000;
const SKIP_SHOW_MS = 1500;
const AUTOPLAY_PROBE_MS = 600;
const PLAY_STARTED_TIME = 0.15;
const FADE_MS = 450;
const HIDE_MS = 500;
const REDUCED_FADE_MS = 300;

function prefersReducedMotion() {
  try {
    return window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches === true;
  } catch {
    return false;
  }
}

/** iOS / coarse pointer — muted autoplay often blocked until a gesture. */
function likelyNeedsGesture() {
  if (typeof window === "undefined") return false;
  try {
    if (window.matchMedia?.("(pointer: coarse)")?.matches) return true;
  } catch {
    /* ignore */
  }
  const ua = navigator.userAgent || "";
  return /iPhone|iPad|iPod/i.test(ua) || (/Macintosh/i.test(ua) && "ontouchend" in document);
}

function heroEl() {
  return document.querySelector(
    '[data-testid="home-hero"], [data-testid="home-page"], .home-hero'
  );
}

function isPageReady() {
  if (document.readyState !== "complete") return false;
  return !!heroEl();
}

/**
 * Home-route vault intro — plays on every home visit (no localStorage / TTL gate).
 * Desktop: muted autoplay (src= like hero bg).
 * Mobile/Safari: if autoplay blocked, show TAP TO OPEN immediately — never silent-skip.
 * Skip arms only after playback has actually started + 1.5s.
 * prefers-reduced-motion: skip video, brief fade only.
 */
export const VaultBumper = ({ onComplete }) => {
  const reduced = typeof window !== "undefined" && prefersReducedMotion();
  const [phase, setPhase] = useState(() => {
    if (typeof window === "undefined") return "done";
    return reduced ? "reduced" : "playing";
  });
  const [showSkip, setShowSkip] = useState(false);
  const [exiting, setExiting] = useState(false);
  const [skipArmed, setSkipArmed] = useState(false);
  const [needsGesture, setNeedsGesture] = useState(false);
  const [mediaFailed, setMediaFailed] = useState(false);

  const phaseRef = useRef(phase);
  const videoRef = useRef(null);
  const videoEndedRef = useRef(reduced);
  const pageReadyRef = useRef(false);
  const skipReqRef = useRef(false);
  const skipArmedRef = useRef(false);
  const exitingRef = useRef(false);
  const doneRef = useRef(phase === "done");
  const notifiedRef = useRef(false);
  const needsGestureRef = useRef(false);
  const playStartedRef = useRef(false);
  const skipArmTimerRef = useRef(null);
  const mediaFailedRef = useRef(false);

  phaseRef.current = phase;
  skipArmedRef.current = skipArmed;
  needsGestureRef.current = needsGesture;

  const notifyComplete = useCallback(() => {
    if (notifiedRef.current) return;
    notifiedRef.current = true;
    onComplete?.();
  }, [onComplete]);

  const finishUnmount = useCallback(() => {
    if (doneRef.current) return;
    doneRef.current = true;
    setPhase("done");
    notifyComplete();
  }, [notifyComplete]);

  const beginExit = useCallback(() => {
    if (exitingRef.current || doneRef.current) return;
    exitingRef.current = true;
    setExiting(true);

    const hero = document.querySelector('[data-testid="home-hero"], .home-hero');
    if (hero) hero.classList.add("hero-intro-reveal");

    notifyComplete();

    const fadeMs = phaseRef.current === "reduced" ? REDUCED_FADE_MS : FADE_MS;
    const hideMs = phaseRef.current === "reduced" ? REDUCED_FADE_MS : HIDE_MS;
    window.setTimeout(() => {
      const root = document.querySelector('[data-testid="vault-bumper"]');
      if (root) root.style.display = "none";
      finishUnmount();
    }, Math.max(hideMs, fadeMs));
  }, [finishUnmount, notifyComplete]);

  const tryExit = useCallback(() => {
    if (exitingRef.current || doneRef.current) return;
    // Waiting for TAP TO OPEN — do not auto-dismiss (unless reduced-motion path).
    if (needsGestureRef.current && !playStartedRef.current && !skipReqRef.current) {
      return;
    }
    const videoDone = videoEndedRef.current || skipReqRef.current;
    if (videoDone && pageReadyRef.current) beginExit();
  }, [beginExit]);

  const freezeVideo = () => {
    const v = videoRef.current;
    if (!v) return;
    try {
      v.pause();
    } catch {
      /* ignore */
    }
  };

  const clearSkipArmTimer = () => {
    if (skipArmTimerRef.current != null) {
      window.clearTimeout(skipArmTimerRef.current);
      skipArmTimerRef.current = null;
    }
  };

  const armSkipAfterDelay = useCallback(() => {
    if (skipArmedRef.current || exitingRef.current || doneRef.current) return;
    clearSkipArmTimer();
    skipArmTimerRef.current = window.setTimeout(() => {
      if (exitingRef.current || doneRef.current || skipReqRef.current) return;
      if (!playStartedRef.current) return;
      skipArmedRef.current = true;
      setSkipArmed(true);
      setShowSkip(true);
    }, SKIP_SHOW_MS);
  }, []);

  const markPlayStarted = useCallback(() => {
    if (playStartedRef.current) return;
    playStartedRef.current = true;
    needsGestureRef.current = false;
    setNeedsGesture(false);
    armSkipAfterDelay();
  }, [armSkipAfterDelay]);

  const showTapCta = useCallback(() => {
    if (playStartedRef.current || exitingRef.current || doneRef.current) return;
    needsGestureRef.current = true;
    setNeedsGesture(true);
  }, []);

  const requestSkip = useCallback(() => {
    if (!skipArmedRef.current) return;
    if (exitingRef.current || doneRef.current || skipReqRef.current) return;
    skipReqRef.current = true;
    freezeVideo();
    tryExit();
  }, [tryExit]);

  const onVideoEnded = () => {
    videoEndedRef.current = true;
    freezeVideo();
    tryExit();
  };

  /** Media error: surface TAP TO OPEN (poster stays). Do NOT silent-skip. */
  const onVideoError = () => {
    mediaFailedRef.current = true;
    setMediaFailed(true);
    showTapCta();
  };

  const applyMutedInline = (v) => {
    if (!v) return;
    v.muted = true;
    v.defaultMuted = true;
    v.setAttribute("muted", "");
    v.playsInline = true;
    v.setAttribute("playsinline", "");
    v.setAttribute("webkit-playsinline", "");
  };

  const tryPlay = useCallback(() => {
    const v = videoRef.current;
    if (!v || phaseRef.current !== "playing" || skipReqRef.current || videoEndedRef.current) {
      return Promise.resolve(false);
    }
    try {
      applyMutedInline(v);
      const p = v.play();
      if (p && typeof p.then === "function") {
        return p
          .then(() => {
            if (v.currentTime > PLAY_STARTED_TIME || !v.paused) {
              markPlayStarted();
            }
            return true;
          })
          .catch(() => {
            showTapCta();
            return false;
          });
      }
      if (!v.paused) markPlayStarted();
      else showTapCta();
      return Promise.resolve(!v.paused);
    } catch {
      showTapCta();
      return Promise.resolve(false);
    }
  }, [markPlayStarted, showTapCta]);

  /**
   * User-gesture: first tap while needsGesture plays the bumper (does NOT skip).
   * If media already failed / play still blocked after gesture: open vault (user action).
   */
  const playFromGesture = useCallback(
    (e) => {
      e?.stopPropagation?.();
      e?.preventDefault?.();
      if (exitingRef.current || doneRef.current || skipReqRef.current) return;

      // Broken asset after user already tried — honor tap as "open vault"
      if (mediaFailedRef.current && needsGestureRef.current) {
        skipReqRef.current = true;
        videoEndedRef.current = true;
        pageReadyRef.current = true;
        beginExit();
        return;
      }

      needsGestureRef.current = false;
      setNeedsGesture(false);
      const v = videoRef.current;
      if (!v) {
        // No element — treat as open
        skipReqRef.current = true;
        videoEndedRef.current = true;
        pageReadyRef.current = true;
        beginExit();
        return;
      }
      try {
        applyMutedInline(v);
        const p = v.play();
        if (p && typeof p.then === "function") {
          p.then(() => markPlayStarted()).catch(() => {
            // Still blocked after gesture with a valid element — open vault
            skipReqRef.current = true;
            videoEndedRef.current = true;
            pageReadyRef.current = true;
            beginExit();
          });
        } else if (!v.paused) {
          markPlayStarted();
        } else {
          showTapCta();
        }
      } catch {
        showTapCta();
      }
    },
    [markPlayStarted, beginExit, showTapCta]
  );

  const onOverlayPointer = useCallback(
    (e) => {
      if (phase === "reduced") return;
      if (needsGestureRef.current && !playStartedRef.current) {
        playFromGesture(e);
        return;
      }
      if (skipArmedRef.current) {
        requestSkip();
      }
    },
    [phase, playFromGesture, requestSkip]
  );

  useEffect(() => {
    if (phase === "done") return undefined;

    const markReady = () => {
      if (pageReadyRef.current) return;
      if (!isPageReady()) return;
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (pageReadyRef.current) return;
          pageReadyRef.current = true;
          tryExit();
        });
      });
    };

    markReady();
    window.addEventListener("load", markReady);
    const poll = window.setInterval(markReady, 120);
    // Safety: only force pageReady. Never force-complete while waiting for TAP.
    const safety = window.setTimeout(() => {
      pageReadyRef.current = true;
      if (needsGestureRef.current && !playStartedRef.current) {
        // Keep overlay + TAP CTA; do not silent-skip.
        return;
      }
      if (!playStartedRef.current && !skipReqRef.current) {
        // Desktop autoplay stuck with no CTA somehow — surface TAP rather than skip.
        showTapCta();
        return;
      }
      videoEndedRef.current = true;
      beginExit();
    }, SAFETY_MS);

    return () => {
      window.removeEventListener("load", markReady);
      window.clearInterval(poll);
      window.clearTimeout(safety);
    };
  }, [phase, tryExit, beginExit, showTapCta]);

  useEffect(() => {
    if (phase === "done" || phase === "reduced") return undefined;
    if (!skipArmed) return undefined;

    const onKey = () => requestSkip();
    const onWheel = () => requestSkip();
    const onScroll = () => requestSkip();

    window.addEventListener("keydown", onKey);
    window.addEventListener("wheel", onWheel, { passive: true });
    window.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("wheel", onWheel);
      window.removeEventListener("scroll", onScroll);
    };
  }, [phase, skipArmed, requestSkip]);

  useEffect(() => {
    if (phase !== "reduced") return undefined;
    videoEndedRef.current = true;
    tryExit();
    return undefined;
  }, [phase, tryExit]);

  // Autoplay kick + fast probe → TAP TO OPEN (esp. iOS Safari)
  useEffect(() => {
    if (phase !== "playing") return undefined;
    let n = 0;
    let cancelled = false;

    // Optimistic: coarse/iOS show CTA early so Lynn never stares at a black screen.
    if (likelyNeedsGesture()) {
      window.setTimeout(() => {
        if (!cancelled && !playStartedRef.current) showTapCta();
      }, 200);
    }

    const kick = () => {
      if (cancelled || playStartedRef.current) return;
      tryPlay();
      n += 1;
      if (n < 12 && phaseRef.current === "playing" && !videoEndedRef.current && !playStartedRef.current) {
        window.setTimeout(kick, 80 * n);
      }
    };
    kick();

    const probe = window.setTimeout(() => {
      if (cancelled || playStartedRef.current || exitingRef.current || doneRef.current) return;
      const v = videoRef.current;
      if (!v) {
        showTapCta();
        return;
      }
      const stuck = v.paused && (v.currentTime || 0) < 0.05;
      if (stuck) {
        showTapCta();
      } else if (!v.paused || (v.currentTime || 0) > PLAY_STARTED_TIME) {
        markPlayStarted();
      }
    }, AUTOPLAY_PROBE_MS);

    const onVis = () => {
      if (document.visibilityState === "visible" && !playStartedRef.current) tryPlay();
    };
    document.addEventListener("visibilitychange", onVis);

    return () => {
      cancelled = true;
      window.clearTimeout(probe);
      clearSkipArmTimer();
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [phase, tryPlay, markPlayStarted, showTapCta]);

  const onPlaying = () => {
    markPlayStarted();
  };

  const onTimeUpdate = () => {
    const v = videoRef.current;
    if (!v || playStartedRef.current) return;
    if ((v.currentTime || 0) > PLAY_STARTED_TIME) markPlayStarted();
  };

  if (phase === "done") return null;

  const overlayClass = [
    "vault-intro",
    exiting ? "vault-intro--exiting" : "vault-intro--playing",
    phase === "reduced" ? "vault-intro--reduced" : "",
    needsGesture ? "vault-intro--needs-gesture" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div
      data-testid="vault-bumper"
      className={overlayClass}
      aria-hidden="true"
      onClick={phase === "reduced" ? undefined : onOverlayPointer}
    >
      {phase === "playing" && (
        <video
          ref={videoRef}
          data-testid="vault-bumper-video"
          className="vault-intro__bumper"
          src={MP4_SRC}
          poster={POSTER_SRC}
          autoPlay
          muted
          playsInline
          preload="auto"
          disablePictureInPicture
          controls={false}
          onEnded={onVideoEnded}
          onError={onVideoError}
          onLoadedData={tryPlay}
          onCanPlay={tryPlay}
          onPlaying={onPlaying}
          onTimeUpdate={onTimeUpdate}
        />
      )}
      {phase === "playing" && needsGesture && !showSkip && (
        <button
          type="button"
          data-testid="vault-bumper-tap"
          className="vault-intro__tap"
          onClick={playFromGesture}
        >
          {mediaFailed ? "TAP TO OPEN" : "TAP TO OPEN"}
        </button>
      )}
      {phase === "playing" && showSkip && (
        <button
          type="button"
          data-testid="vault-bumper-skip"
          className="vault-intro__skip"
          onClick={(e) => {
            e.stopPropagation();
            requestSkip();
          }}
        >
          SKIP
        </button>
      )}
    </div>
  );
};

/** App already gates to `/`; return true unless reduced-motion (bumper will brief-fade). */
export function shouldShowVaultIntro() {
  if (typeof window === "undefined") return false;
  return !prefersReducedMotion();
}
